import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useTenantAuth } from "@/context/TenantAuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { Loader2, Check, PenLine, FileText } from "lucide-react";

const tfile = (name) => (name ? `${API}/tenant/files/${name}` : "");

function StatusBadge({ status }) {
  const { t } = useI18n();
  const map = {
    approved: ["承認済み", "bg-green-500/15 text-green-400 border-green-500/30"],
    submitted: ["提出済み", "bg-amber-500/15 text-amber-400 border-amber-500/30"],
  };
  const [label, cls] = map[status] || ["未提出", "bg-white/10 text-gray-400 border-white/15"];
  return <span className={`text-xs px-2.5 py-1 rounded-full border ${cls}`}>{t(label)}</span>;
}

function ImgBox({ label, name, testid }) {
  const { t } = useI18n();
  return (
    <div data-testid={testid}>
      <div className="text-xs text-gray-500 mb-1.5">{t(label)}</div>
      <div className="flex items-center justify-center h-32 rounded-lg border border-white/15 bg-[#1b1b1b] overflow-hidden">
        {name ? <img src={tfile(name)} alt="" className="w-full h-full object-contain" /> : <span className="text-xs text-gray-600">{t("未登録")}</span>}
      </div>
    </div>
  );
}

function ReadRow({ label, value }) {
  const { t } = useI18n();
  return (
    <div>
      <div className="text-xs text-gray-500 mb-0.5">{t(label)}</div>
      <div className="text-sm text-gray-100 break-words">{value || <span className="text-gray-600">{t("未入力")}</span>}</div>
    </div>
  );
}

async function uploadSig(blob) {
  const fd = new FormData();
  fd.append("file", new File([blob], "signature.png", { type: "image/png" }));
  const { data } = await api.post("/tenant/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.stored_name;
}

const fmtYen = (n) => (n || n === 0) ? `¥${Number(n).toLocaleString()}` : "—";

const CLAUSES = [
  "【契約期間・更新】契約期間は上記のとおりとし、期間満了時は協議のうえ更新できます。",
  "【支払方法・支払期日】賃料等は毎月定められた期日までに、下記の方法でお支払いください。",
  "【遅延損害金】支払を遅延した場合、年14.6%の割合による遅延損害金が発生することがあります。",
  "【解約予告】解約する場合は、原則として1ヶ月前までに管理会社へご通知ください。",
  "【原状回復】退去時は、通常損耗を除き、借主の故意・過失による損傷の原状回復義務を負います。",
  "【禁止事項】無断転貸・用法違反・近隣への迷惑行為等を禁止します。",
  "【保証】連帯保証人または保証会社のご利用が必要となる場合があります。",
];

function ContractTerms({ contract }) {
  const { t } = useI18n();
  const fees = [
    ["賃料", contract.rent], ["共益費", contract.common_fee], ["管理費", contract.management_fee],
    ["駐車場料金", contract.parking_fee], ["その他費用", contract.other_fee],
    ["敷金", contract.deposit], ["礼金", contract.key_money], ["更新料", contract.renewal_fee],
  ];
  const p = contract.payment || {};
  const bank = ["bank_name", "branch_name", "bank_account_type", "account_number", "account_holder"].map((k) => p[k]).filter(Boolean).join(" ");
  const payRows = [];
  if (p.pay_method) payRows.push(["支払方法", p.pay_method]);
  if (p.pay_due) payRows.push(["支払期日", p.pay_due]);
  if (bank) payRows.push(["振込先", bank]);
  if (p.late_fee_rate !== undefined && p.late_fee_rate !== null && p.late_fee_rate !== "") payRows.push(["遅延損害金", `年${p.late_fee_rate}%`]);
  return (
    <div className="rounded-lg border border-white/10 bg-[#161616] p-4 space-y-4" data-testid="contract-terms">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-200 font-medium">{t("契約条件（重要事項）")}</div>
        <div className="flex gap-2">
          <a href={`${API}/tenant/contracts/${contract.id}/important-matters-pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs border border-white/15 text-gray-200 px-3 py-1.5 rounded-md hover:border-white/30" data-testid="terms-imed-pdf">
            <FileText className="w-3.5 h-3.5" />{t("重要事項説明書")}
          </a>
          <a href={`${API}/tenant/contracts/${contract.id}/lease-pdf`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs border border-white/15 text-gray-200 px-3 py-1.5 rounded-md hover:border-white/30" data-testid="terms-lease-pdf">
            <FileText className="w-3.5 h-3.5" />{t("賃貸借契約書")}
          </a>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {fees.map(([l, v]) => (
          <div key={l}>
            <div className="text-xs text-gray-500">{t(l)}</div>
            <div className="text-sm text-gray-100">{fmtYen(v)}</div>
          </div>
        ))}
      </div>
      <div>
        <div className="text-xs text-gray-500">{t("契約期間")}</div>
        <div className="text-sm text-gray-100">{contract.start_date || "—"} 〜 {contract.end_date || "—"}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">{t("支払方法・振込先")}</div>
        {payRows.length || contract.payment_info ? (
          <div className="text-sm text-gray-100 space-y-0.5" data-testid="terms-payment">
            {payRows.map(([l, v]) => <div key={l}><span className="text-gray-500">{t(l)}：</span>{v}</div>)}
            {contract.payment_info && <div className="whitespace-pre-wrap text-gray-300 mt-1">{contract.payment_info}</div>}
          </div>
        ) : (
          <div className="text-sm text-gray-400" data-testid="terms-payment">{t("お支払い方法は管理会社よりご案内します。")}</div>
        )}
      </div>
      {contract.note && (
        <div>
          <div className="text-xs text-gray-500">{t("特約・注意事項")}</div>
          <div className="text-sm text-gray-100 whitespace-pre-wrap">{contract.note}</div>
        </div>
      )}
      <div>
        <div className="text-xs text-gray-500 mb-1.5">{t("契約条項")}</div>
        <ul className="space-y-1.5 text-xs text-gray-300 leading-relaxed list-none">
          {CLAUSES.map((cl, i) => <li key={i}>{t(cl)}</li>)}
        </ul>
      </div>
    </div>
  );
}

function SignaturePad({ onChange }) {
  const { t } = useI18n();
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#111111";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: (p.clientX - r.left) * (c.width / r.width), y: (p.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const { x, y } = pos(e); const ctx = canvasRef.current.getContext("2d"); ctx.lineTo(x, y); ctx.stroke(); dirty.current = true; };
  const end = () => { if (!drawing.current) return; drawing.current = false; if (dirty.current) canvasRef.current.toBlob((b) => onChange(b), "image/png"); };
  const clear = () => { const c = canvasRef.current; const ctx = c.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height); dirty.current = false; onChange(null); };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-sm text-gray-300 flex items-center gap-1.5"><PenLine className="w-4 h-4" />{t("サイン")}</label>
        <button type="button" onClick={clear} className="text-xs text-gray-400 hover:text-white" data-testid="tenant-signature-clear">{t("サインをクリア")}</button>
      </div>
      <canvas
        ref={canvasRef}
        width={600}
        height={180}
        className="w-full rounded-lg border border-white/15 bg-white touch-none"
        data-testid="tenant-signature-pad"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
      />
    </div>
  );
}

function AgreementForm({ contract, onDone }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const ag = contract.agreement || {};
  const readOnly = ag.status === "approved" || ag.status === "submitted";
  const [profile, setProfile] = useState(null);
  const [sigBlob, setSigBlob] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [confirmedTerms, setConfirmedTerms] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (readOnly) return;
    api.get("/tenant/profile").then(({ data }) => setProfile(data)).catch(() => setProfile({}));
  }, [readOnly]);

  // 承認済み: 提出済みスナップショットを読み取り表示
  if (readOnly) {
    const type = ag.account_type || "individual";
    const info = type === "individual" ? (ag.individual || {}) : (ag.corporate || {});
    return (
      <div className="space-y-6" data-testid="agreement-form">
        <div className="text-sm text-green-400 flex items-center gap-2"><Check className="w-4 h-4" />{t(ag.status === "approved" ? "この契約は承認済みです" : "署名が確定しました。内容は編集できません。")}</div>
        <ContractTerms contract={contract} />
        <div className="rounded-lg border border-white/10 bg-[#161616] p-4">
          <div className="text-xs text-gray-500 mb-3">{t(type === "individual" ? "個人" : "法人")}</div>
          <div className="grid sm:grid-cols-2 gap-4">
            {type === "individual" ? (<>
              <ReadRow label="氏名" value={info.name} />
              <ReadRow label="生年月日" value={info.birth_date} />
              <ReadRow label="電話番号" value={info.phone} />
              <ReadRow label="住所" value={info.address} />
            </>) : (<>
              <ReadRow label="会社名" value={info.company_name} />
              <ReadRow label="登記番号" value={info.registration_number} />
              <ReadRow label="代表者名" value={info.representative_name} />
              <ReadRow label="担当者名" value={info.contact_name} />
            </>)}
          </div>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <ImgBox label={type === "corporate" ? "担当者写真" : "本人写真"} name={ag.photo} testid="agreement-view-photo" />
            <ImgBox label="身分証明書（表）" name={ag.id_front} testid="agreement-view-id-front" />
            <ImgBox label="身分証明書（裏）" name={ag.id_back} testid="agreement-view-id-back" />
          </div>
        </div>
        {ag.signature && (
          <div>
            <label className="block text-sm text-gray-300 mb-1.5">{t("サイン")}</label>
            <img src={tfile(ag.signature)} alt="signature" className="rounded-lg border border-white/15 bg-white max-h-40" />
          </div>
        )}
      </div>
    );
  }

  if (!profile) {
    return <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" />{t("読み込み中...")}</div>;
  }

  const type = profile.account_type === "corporate" ? "corporate" : "individual";
  const photo = profile.photo || null;
  const idFront = profile.doc_identity_front || null;
  const idBack = profile.doc_identity_back || null;
  const foreign = profile.nationality && profile.nationality !== "日本";
  const resFront = profile.doc_residence_front || null;
  const resBack = profile.doc_residence_back || null;

  const missing = [];
  if (type === "individual") {
    if (!profile.name) missing.push("氏名");
  } else {
    if (!profile.company_name) missing.push("会社名");
    if (!profile.representative_name) missing.push("代表者名");
  }
  if (!photo) missing.push(type === "corporate" ? "担当者写真" : "本人写真");
  if (!idFront || !idBack) missing.push("身分証明書（表・裏）");
  if (foreign && (!resFront || !resBack)) missing.push("在留カード（表・裏）");

  const goProfile = () => navigate("/tenant/profile");

  const submit = async () => {
    if (missing.length) { toast.error(t("プロフィールの入力が不足しています")); return; }
    if (!sigBlob) { toast.error(t("サインを入力してください")); return; }
    if (!confirmedTerms) { toast.error(t("契約条件（重要事項）の確認が必要です")); return; }
    if (!agreed) { toast.error(t("契約内容への同意が必要です")); return; }
    setSaving(true);
    try {
      const signature = await uploadSig(sigBlob);
      const body = {
        account_type: type,
        individual: type === "individual"
          ? { name: profile.name, birth_date: profile.birth_date, phone: profile.phone, address: profile.current_address }
          : {},
        corporate: type === "corporate"
          ? { company_name: profile.company_name, registration_number: profile.registration_number, representative_name: profile.representative_name, contact_name: profile.contact_name }
          : {},
        photo, id_front: idFront, id_back: idBack, signature,
      };
      await api.post(`/tenant/contracts/${contract.id}/agreement`, body);
      toast.success(t("提出しました"));
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "提出に失敗しました");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="agreement-form">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm text-gray-200 font-medium">{t("プロフィール情報を自動反映しています")}</div>
          <p className="text-xs text-gray-500 mt-0.5">{t("内容の修正はプロフィールから行ってください。")}</p>
        </div>
        <button type="button" onClick={goProfile} className="text-sm border border-white/15 text-gray-200 px-4 py-2 rounded-md hover:border-white/30 transition-colors inline-flex items-center gap-1.5" data-testid="agreement-edit-profile">
          <PenLine className="w-3.5 h-3.5" />{t("プロフィールを修正")}
        </button>
      </div>

      <div className="rounded-lg border border-white/10 bg-[#161616] p-4">
        <div className="text-xs text-gray-500 mb-3">{t(type === "individual" ? "個人" : "法人")}</div>
        <div className="grid sm:grid-cols-2 gap-4">
          {type === "individual" ? (<>
            <ReadRow label="氏名" value={profile.name} />
            <ReadRow label="生年月日" value={profile.birth_date} />
            <ReadRow label="電話番号" value={profile.phone} />
            <ReadRow label="住所" value={profile.current_address} />
          </>) : (<>
            <ReadRow label="会社名" value={profile.company_name} />
            <ReadRow label="登記番号" value={profile.registration_number} />
            <ReadRow label="代表者名" value={profile.representative_name} />
            <ReadRow label="担当者名" value={profile.contact_name} />
          </>)}
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-4">
          <ImgBox label={type === "corporate" ? "担当者写真" : "本人写真"} name={photo} testid="agreement-doc-photo" />
          <ImgBox label="身分証明書（表）" name={idFront} testid="agreement-doc-id-front" />
          <ImgBox label="身分証明書（裏）" name={idBack} testid="agreement-doc-id-back" />
          {foreign && <ImgBox label="在留カード（表）" name={resFront} testid="agreement-doc-res-front" />}
          {foreign && <ImgBox label="在留カード（裏）" name={resBack} testid="agreement-doc-res-back" />}
        </div>
      </div>

      <ContractTerms contract={contract} />

      {missing.length > 0 && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300" data-testid="agreement-missing">
          {t("以下の項目が未入力です")}: {missing.map((m) => t(m)).join("、")}
          <button type="button" onClick={goProfile} className="ml-2 underline hover:text-amber-200" data-testid="agreement-missing-link">{t("プロフィールを修正")}</button>
        </div>
      )}

      <SignaturePad onChange={setSigBlob} />

      <label className="flex items-start gap-2.5 text-sm text-gray-300 cursor-pointer" data-testid="agreement-confirm-terms">
        <input type="checkbox" checked={confirmedTerms} onChange={(e) => setConfirmedTerms(e.target.checked)} className="mt-1 accent-brand" data-testid="agreement-confirm-terms-check" />
        <span>{t("上記の契約条件（重要事項）を確認しました。")}</span>
      </label>

      <label className="flex items-start gap-2.5 text-sm text-gray-300 cursor-pointer" data-testid="agreement-agree">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 accent-brand" data-testid="agreement-agree-check" />
        <span>{t("上記の契約内容とプロフィール情報を確認し、同意します。")}</span>
      </label>

      <button onClick={submit} disabled={saving || missing.length > 0}
        className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-medium px-6 py-3.5 rounded-md transition-colors"
        data-testid="agreement-submit">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
        {t("署名して提出する")}
      </button>
    </div>
  );
}

export default function TenantPortal() {
  const { tenant, logout } = useTenantAuth();
  const { t } = useI18n();
  const [contracts, setContracts] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/tenant/contracts");
      setContracts(data);
    } catch { setContracts([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const money = (n) => (n || n === 0) ? `¥${Number(n).toLocaleString()}` : "-";

  return (
    <div data-testid="tenant-portal">
        <h1 className="font-display text-2xl font-light mb-6">{t("デジタル賃貸契約")}</h1>

        {contracts === null ? (
          <div className="text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />{t("読み込み中...")}</div>
        ) : contracts.length === 0 ? (
          <div className="text-gray-500 border border-white/10 rounded-lg p-10 text-center">{t("契約がまだ登録されていません。")}</div>
        ) : (
          <div className="space-y-4" data-testid="tenant-contracts">
            {contracts.map((c) => (
              <div key={c.id} className="bg-[#161616] border border-white/10 rounded-lg p-6" data-testid={`tenant-contract-${c.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 text-gray-400 text-xs mb-1"><FileText className="w-3.5 h-3.5" />{c.property?.name || t("物件")} {c.room?.room_number ? `／ ${c.room.room_number}` : ""}</div>
                    <div className="text-lg font-medium">{money(c.rent)}<span className="text-sm text-gray-500 ml-1">/ {t("月")}</span></div>
                    <div className="text-xs text-gray-500 mt-1">{c.start_date || "-"} 〜 {c.end_date || "-"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={c.agreement?.status} />
                    {c.agreement?.status === "approved" && (
                      <a href={`${API}/tenant/contracts/${c.id}/pdf`} target="_blank" rel="noreferrer"
                        className="text-sm border border-white/15 text-gray-200 px-4 py-2 rounded-md hover:border-white/30 transition-colors"
                        data-testid={`tenant-contract-pdf-${c.id}`}>
                        {t("契約書PDF")}
                      </a>
                    )}
                    <button onClick={() => setActive(active === c.id ? null : c.id)}
                      className="text-sm bg-brand hover:bg-brand-hover text-white px-4 py-2 rounded-md transition-colors"
                      data-testid={`tenant-contract-open-${c.id}`}>
                      {(c.agreement?.status === "approved" || c.agreement?.status === "submitted") ? t("内容を確認") : t("契約を確認・署名")}
                    </button>
                  </div>
                </div>
                {active === c.id && (
                  <div className="mt-6 pt-6 border-t border-white/10">
                    <AgreementForm contract={c} onDone={() => { setActive(null); load(); }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
